import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../lib/api";
import { EmptyState } from "../components/EmptyState";
import { SkeletonLine } from "../components/Skeleton";
import { useToast } from "../components/Toast";
import { useConfirm } from "../components/ConfirmDialog";
import type { UserRecord, UserRole } from "../types";

function roleLabel(role: UserRole): string {
  if (role === "valuation_analyst") return "Analyst";
  if (role === "admin") return "Admin";
  return "Client";
}

export default function AdminUsers() {
  const toast = useToast();
  const confirm = useConfirm();
  const [users, setUsers] = useState<UserRecord[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [filterRole, setFilterRole] = useState<UserRole | "all">("all");
  const [filterActive, setFilterActive] = useState<"all" | "active" | "inactive">("active");

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (filterRole !== "all") params.set("role", filterRole);
    if (filterActive !== "all") params.set("active", filterActive === "active" ? "true" : "false");
    try {
      const data = await api.get<{ users: UserRecord[] }>(`/api/admin/users?${params.toString()}`);
      setUsers(data.users);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "Failed to load users";
      setLoadErr(msg);
      toast.error("Users failed to load", msg);
    }
  }, [filterRole, filterActive, toast]);

  useEffect(() => { setUsers(null); void load(); }, [load]);

  const handleToggle = async (u: UserRecord) => {
    const verb = u.active ? "Deactivate" : "Reactivate";
    const ok = await confirm({
      title: `${verb} ${u.email}?`,
      body: u.active
        ? "Deactivated users cannot sign in. You can reactivate them at any time."
        : "Reactivating allows this user to sign in again.",
      confirmLabel: verb,
      tone: u.active ? "danger" : "default",
    });
    if (!ok) return;
    try {
      await api.patch(`/api/admin/users/${u.id}`, { active: !u.active });
      toast.success(`${u.email} ${u.active ? "deactivated" : "reactivated"}`);
      await load();
    } catch (e) {
      toast.error("Update failed", e instanceof ApiError ? e.message : "Unknown error");
    }
  };

  const handleReset = async (u: UserRecord) => {
    const ok = await confirm({
      title: `Reset password for ${u.email}?`,
      body: (
        <>
          Their current password will be cleared and a fresh invitation link will be emailed. They won't be
          able to sign in until they set a new password.
        </>
      ),
      confirmLabel: "Send reset link",
    });
    if (!ok) return;
    try {
      const resp = await api.post<{ inviteLink: string; emailDelivered: boolean }>(
        `/api/admin/users/${u.id}/reset`,
      );
      if (resp.emailDelivered) {
        toast.success("Reset link emailed", `Sent to ${u.email}.`);
      } else {
        toast.warning("Link created, email failed", "Share the invite link manually.", );
      }
    } catch (e) {
      toast.error("Reset failed", e instanceof ApiError ? e.message : "Unknown error");
    }
  };

  return (
    <div>
      <div className="admin-content-header">
        <div>
          <div className="admin-content-title">Users</div>
          <div className="admin-content-sub">
            All users across the platform &mdash; admin, analyst, client.
          </div>
        </div>
      </div>

      {loadErr && <div className="alert alert-error" role="alert">{loadErr}</div>}

      <div className="filter-row">
        <div className="form-group">
          <label htmlFor="filter-role">Role</label>
          <select
            id="filter-role"
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value as UserRole | "all")}
          >
            <option value="all">All roles</option>
            <option value="admin">Admin</option>
            <option value="valuation_analyst">Valuation Analyst</option>
            <option value="client">Client</option>
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="filter-active">Status</label>
          <select
            id="filter-active"
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value as typeof filterActive)}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      {!users ? (
        <LoadingTable />
      ) : users.length === 0 ? (
        <EmptyState
          heading="No users match these filters"
          body="Adjust the filters above to see more users, or invite someone new from their client page."
        />
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Display name</th>
              <th>Role</th>
              <th>Last login</th>
              <th>Status</th>
              <th style={{ width: 220 }}></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.email}</td>
                <td>{u.displayName}</td>
                <td>
                  <span className={`badge badge-${u.role === "valuation_analyst" ? "analyst" : u.role}`}>
                    {roleLabel(u.role)}
                  </span>
                </td>
                <td className="text-muted num">
                  {u.lastLogin
                    ? new Date(u.lastLogin).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
                    : "—"}
                </td>
                <td>
                  <span className={`badge badge-${u.active ? "active" : "inactive"}`}>
                    {u.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td>
                  <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end" }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => handleReset(u)}>Reset pw</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => handleToggle(u)}>
                      {u.active ? "Deactivate" : "Reactivate"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function LoadingTable() {
  return (
    <table className="data-table" aria-busy="true">
      <thead>
        <tr>
          <th>Email</th>
          <th>Display name</th>
          <th>Role</th>
          <th>Last login</th>
          <th>Status</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {[0, 1, 2, 3].map((i) => (
          <tr key={i}>
            <td><SkeletonLine width="80%" /></td>
            <td><SkeletonLine width="60%" /></td>
            <td><SkeletonLine width="40%" /></td>
            <td><SkeletonLine width="55%" /></td>
            <td><SkeletonLine width="45%" /></td>
            <td><SkeletonLine width="60%" /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
